/**
 * A baby's age, from the birth date the parents set. Both dates are calendar
 * dates (YYYY-MM-DD), never instants: a birthday is the same date in every
 * zone, so reading one as midnight UTC can't show it a day early. "Today" is
 * the caller's: the phone's own day, or the household's for the assistant
 * (`Calendar.isoDate`).
 */
import { DAY_MS } from "./calendar.ts";

/** Babia is a newborn log: a birth date further back than this is a mistyped year. */
export const MAX_AGE_YEARS = 5;

/** Year, month (1–12), and day of a YYYY-MM-DD date. */
function parts(date: string): [number, number, number] {
  const [year, month, day] = date.split("-").map(Number);
  return [year!, month!, day!];
}

/** Midnight UTC starting the date; years 0–99 stay themselves. */
export function utcDay(date: string): number {
  const [year, month, day] = parts(date);
  const d = new Date(0);
  d.setUTCFullYear(year, month - 1, day);
  return d.getTime();
}

/**
 * The earliest birth date Babia takes on `today`: the same date
 * `MAX_AGE_YEARS` back (Mar 1 when that year has no Feb 29).
 */
export function earliestBirthDate(today: string): string {
  const [year, month, day] = parts(today);
  const d = new Date(0);
  d.setUTCFullYear(year - MAX_AGE_YEARS, month - 1, day);
  return d.toISOString().slice(0, 10);
}

/** Whole days from `birthDate` to `today`: 0 on the day of birth, never negative. */
export function ageInDays(birthDate: string, today: string): number {
  return Math.max(0, Math.round((utcDay(today) - utcDay(birthDate)) / DAY_MS));
}

/** Days in `month` (1–12) of `year`. */
function daysInMonth(year: number, month: number): number {
  const d = new Date(0);
  d.setUTCFullYear(year, month, 0);
  return d.getUTCDate();
}

/**
 * Whole calendar months from `birthDate` to `today`: one on the same date next
 * month, or on its last day when it is shorter (born Jan 31, a month old Feb 28).
 */
export function ageInMonths(birthDate: string, today: string): number {
  const [by, bm, bd] = parts(birthDate);
  const [ty, tm, td] = parts(today);
  const monthday = Math.min(bd, daysInMonth(ty, tm));
  return Math.max(0, (ty - by) * 12 + (tm - bm) - (td < monthday ? 1 : 0));
}

/**
 * The date month `n` of life starts (0 is the birth date): the same date `n`
 * months on, or that month's last day when it is shorter, so born Jan 31 is a
 * month old on Feb 28 and two months old on Mar 31, as `ageInMonths` counts.
 */
export function monthOfLifeStart(birthDate: string, n: number): string {
  const [by, bm, bd] = parts(birthDate);
  const months = bm - 1 + n;
  const year = by + Math.floor(months / 12);
  const month = (((months % 12) + 12) % 12) + 1;
  const d = new Date(0);
  d.setUTCFullYear(year, month - 1, Math.min(bd, daysInMonth(year, month)));
  return d.toISOString().slice(0, 10);
}

const count = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/**
 * How old, the way a parent or a pediatrician says it: days for the first two
 * weeks, weeks and days until three months, months until two years, then
 * years. "0 days old", "9 days old", "3 weeks 4 days old", "5 months old".
 */
export function ageText(birthDate: string, today: string): string {
  const days = ageInDays(birthDate, today);
  if (days < 14) return `${count(days, "day")} old`;
  const months = ageInMonths(birthDate, today);
  if (months < 3) {
    const rest = days % 7;
    return `${count(Math.floor(days / 7), "week")}${rest ? ` ${count(rest, "day")}` : ""} old`;
  }
  if (months < 24) return `${months} months old`;
  return `${Math.floor(months / 12)} years old`;
}

const DATE = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "Sep 14, 2026" */
export function birthDateText(birthDate: string): string {
  return DATE.format(utcDay(birthDate));
}
